import { useEffect, useMemo } from 'react';
import { Layer, Source, useMap } from 'react-map-gl';
import type { MapLayerMouseEvent } from 'mapbox-gl';
import type { Feature, FeatureCollection, Geometry } from 'geojson';
import CenteredMap from 'components/map/centered-map';
import {
  LTD_SLOT_LINE_PAINT,
  LTD_SLOT_SELECTED_LINE_PAINT,
  LTD_SLOT_WIDTH_EDITING,
} from 'components/map/ltd-slot-paint';
import {
  END_A,
  END_B,
  bottomPickableEnds,
  endpointOf,
  isBottomEndValid,
  nearestPickableEnd,
  type Endpoint,
  type LtdCandidate,
  type LtdEndKey,
  type LtdEndRef,
} from './composite-geometry';
import { pickerMapConfigAtom } from '../../atoms';
import styles from './add-wellbore-tearsheet.module.scss';

/** How near a click has to land to count as picking an endpoint. */
const PICK_TOLERANCE_FT = 2000;

/**
 * Keeps the GL canvas matched to its container.
 *
 * Mapbox measures once at mount, which here happens while the drawer is still animating in
 * — so the canvas keeps its mid-animation size and paints short of the pane. Observing the
 * container covers the open animation and any later layout change. `resize()` never alters
 * the container, so this cannot feed back into itself.
 * @return {null} Renders nothing.
 */
function KeepMapSized() {
  const { current: map } = useMap();

  useEffect(() => {
    if (!map) return undefined;
    const observer = new ResizeObserver(() => map.resize());
    observer.observe(map.getContainer());
    return () => observer.disconnect();
  }, [map]);

  return null;
}

/**
 * One stick as a two-point LineString feature.
 * @param {Endpoint} from One end.
 * @param {Endpoint} to The other.
 * @param {Record<string, unknown>} props Feature properties for the layer's paint expressions.
 * @return {Feature} The feature.
 */
const line = (
  from: Endpoint,
  to: Endpoint,
  props: Record<string, unknown>
): Feature => ({
  type: 'Feature',
  properties: props,
  geometry: {
    type: 'LineString',
    coordinates: [
      [from.lon, from.lat],
      [to.lon, to.lat],
    ],
  },
});

/**
 * Wraps features as a FeatureCollection, which is what a Source takes.
 * @param {Feature[]} features The features.
 * @return {FeatureCollection} The collection.
 */
const collection = (features: Feature[]): FeatureCollection => ({
  type: 'FeatureCollection',
  features,
});

interface LtdSelectionMapProps {
  /** Every stick currently listed, so unselected ones can draw faintly for context. */
  candidates: LtdCandidate[];
  /**
   * What to centre on when nothing is selected. Kept apart from `candidates` so the caller can
   * blank the drawn context while a query is stale without the map recentring. Defaults to
   * `candidates`.
   */
  framingCandidates?: LtdCandidate[];
  selected: LtdCandidate[];
  /** The backend's trajectory for the current inputs, or null before one is derived. */
  trajectory: GeoJSON.LineString | null;
  /** The sticks the backend says it traversed. Only these draw as laterals. */
  orderedMembers: number[];
  surface: LtdEndRef | null;
  bottom: LtdEndRef | null;
  /** Valid bottom end per stick for the current surface hole; null while unknown. */
  validBottomEnds?: Map<number, LtdEndKey> | null;
  /** Armed role, if any — a click then designates the nearest endpoint for it. */
  pickRole: 'surface' | 'bottom' | null;
  onPick: (role: 'surface' | 'bottom', ref: LtdEndRef) => void;
}

/**
 * The sheet's preview map.
 *
 * Draws what the save will build, as the BACKEND derived it: its trajectory dashed underneath,
 * the traversed sticks' laterals solid on top, and the two designated holes as coloured
 * endpoints. The dashed runs the laterals do not cover are the derived ties — invented rather
 * than surveyed, and carrying no perforated length. Nothing here reconstructs them; the
 * laterals simply mask the rest. Unselected sticks draw faintly for context.
 *
 * Clicking while a role is armed designates the endpoint nearest the click. Nearest-point
 * rather than feature hit-testing: the endpoints are small circles on a busy basemap, and a
 * tolerance the reader can actually hit beats an exact hit they cannot.
 * @param {LtdSelectionMapProps} props The sticks, the previewed trajectory, and the pick handlers.
 * @return {JSX.Element} The map.
 */
// eslint-disable-next-line max-lines-per-function -- a map whose body is a flat list of <Source>/<Layer> declarations over one selection, not nested logic.
export function LtdSelectionMap({
  candidates,
  framingCandidates = candidates,
  selected,
  trajectory,
  orderedMembers,
  surface,
  bottom,
  validBottomEnds = null,
  pickRole,
  onPick,
}: LtdSelectionMapProps) {
  const selectedIds = useMemo(
    () => new Set(selected.map((candidate) => candidate.ltdPropertyId)),
    [selected]
  );

  // Every endpoint of every SELECTED stick — what a click can resolve to.
  const pickable = useMemo(
    () =>
      selected.flatMap((candidate) =>
        [END_A, END_B]
          .map((end) => endpointOf(candidate, end))
          .filter((point): point is Endpoint => point !== null)
      ),
    [selected]
  );

  const context = useMemo(
    () =>
      collection(
        candidates
          .filter((candidate) => !selectedIds.has(candidate.ltdPropertyId))
          .flatMap((candidate) => {
            const a = endpointOf(candidate, END_A);
            const b = endpointOf(candidate, END_B);
            return a && b ? [line(a, b, { id: candidate.ltdPropertyId })] : [];
          })
      ),
    [candidates, selectedIds]
  );

  /**
   * The traversed sticks' own laterals, drawn from the raw stick ends.
   *
   * Scoped to `orderedMembers` — the backend's answer for which sticks it actually used — so
   * the same-stick collapse (both holes on one stick drops the rest) is honoured without
   * re-deriving it here.
   */
  const laterals = useMemo(() => {
    const traversed = new Set(orderedMembers);
    return collection(
      selected
        .filter((candidate) => traversed.has(candidate.ltdPropertyId))
        .flatMap((candidate) => {
          const a = endpointOf(candidate, END_A);
          const b = endpointOf(candidate, END_B);
          return a && b
            ? [line(a, b, { slotId: candidate.ltdPropertyId })]
            : [];
        })
    );
  }, [selected, orderedMembers]);

  /**
   * The whole trajectory as the backend derived it.
   *
   * Drawn UNDER the laterals and dashed, so the runs it covers that no lateral sits on read
   * as what they are: derived ties the composite invented, carrying no perforated length.
   * Nothing here reconstructs those segments — the laterals on top simply mask the rest.
   */
  const trajectoryData = useMemo<FeatureCollection>(() => {
    if (!trajectory) return collection([]);
    return collection([
      { type: 'Feature', properties: {}, geometry: trajectory },
    ]);
  }, [trajectory]);

  // The two designated holes, straight from the designations — no chain needed.
  const holes = useMemo<FeatureCollection>(() => {
    /**
     * @param {LtdEndRef | null} ref A designation, or null.
     * @return {Endpoint | null} The end it points at, or null.
     */
    const resolve = (ref: LtdEndRef | null) => {
      if (!ref) return null;
      const candidate = selected.find(
        (entry) => entry.ltdPropertyId === ref.slotId
      );
      return candidate ? endpointOf(candidate, ref.end) : null;
    };

    return collection(
      (
        [
          [resolve(surface), 'surface'],
          [resolve(bottom), 'bottom'],
        ] as [Endpoint | null, string][]
      )
        .filter((entry): entry is [Endpoint, string] => entry[0] !== null)
        .map(([point, role]) => ({
          type: 'Feature',
          properties: { role },
          geometry: { type: 'Point', coordinates: [point.lon, point.lat] },
        }))
    );
  }, [selected, surface, bottom]);

  /**
   * The ends the bottom hole could still be moved to — every end of every non-surface stick
   * other than the designated one — flagged with whether the chain accepts it. An invalid end
   * draws greyed out, and a click on it is ignored. Only meaningful with more than one stick:
   * a lone stick's bottom is simply its far end.
   */
  const bottomCandidates = useMemo<FeatureCollection>(() => {
    if (selected.length < 2) return collection([]);
    return collection(
      bottomPickableEnds(pickable, surface?.slotId)
        // ...less the one already designated, which is drawn by the bottom-hole layer itself.
        .filter(
          (point) =>
            !(point.slotId === bottom?.slotId && point.end === bottom?.end)
        )
        .map((point) => ({
          type: 'Feature',
          properties: {
            valid: isBottomEndValid(validBottomEnds, {
              slotId: point.slotId,
              end: point.end,
            }),
          },
          geometry: { type: 'Point', coordinates: [point.lon, point.lat] },
        }))
    );
  }, [selected.length, pickable, surface, bottom, validBottomEnds]);

  /**
   * What the map frames on: the selection where there is one, else everything listed.
   *
   * A POLYGON, not a MultiPoint — `findCenter` extracts coordinates from Polygon and
   * MultiPolygon only, so any other type yields empty bounds, and mapbox's `getCenter()`
   * THROWS on those rather than returning undefined (findCenter's own `!center` guard never
   * gets to run). A point-shaped geometry therefore crashes the whole card. This builds the
   * bounding ring instead, padded so one stick does not frame to a zero-area box.
   */
  const focus = useMemo<Geometry>(() => {
    const points =
      pickable.length > 0
        ? pickable.map((point): [number, number] => [point.lon, point.lat])
        : framingCandidates.flatMap((candidate) =>
            (candidate.ends ?? []).map((end): [number, number] => [
              end.lon,
              end.lat,
            ])
          );

    // Nothing listed yet — frame the Midland Basin, where the inventory is.
    const box =
      points.length > 0
        ? {
            minLon: Math.min(...points.map(([lon]) => lon)),
            maxLon: Math.max(...points.map(([lon]) => lon)),
            minLat: Math.min(...points.map(([, lat]) => lat)),
            maxLat: Math.max(...points.map(([, lat]) => lat)),
          }
        : { minLon: -102.1, maxLon: -101.7, minLat: 32.3, maxLat: 32.6 };

    const pad = 0.01;
    return {
      type: 'Polygon',
      coordinates: [
        [
          [box.minLon - pad, box.minLat - pad],
          [box.maxLon + pad, box.minLat - pad],
          [box.maxLon + pad, box.maxLat + pad],
          [box.minLon - pad, box.maxLat + pad],
          [box.minLon - pad, box.minLat - pad],
        ],
      ],
    };
  }, [pickable, framingCandidates]);

  /**
   * Picks the stick end nearest the click for whichever role is armed.
   * @param {MapLayerMouseEvent} event The map click.
   */
  const handleClick = (event: MapLayerMouseEvent) => {
    if (!pickRole || pickable.length === 0) return;

    const click: Endpoint = {
      slotId: -1,
      end: END_A,
      lon: event.lngLat.lng,
      lat: event.lngLat.lat,
    };
    /* The same narrowing the bottom-candidate layer draws with. Without it a bottom-role
       click near the junction resolves to the SURFACE stick's far end — the two sit within a
       few feet of each other — and `handleDesignate` answers that by moving the surface to
       another stick, silently reversing the bore. The single-stick case is left alone: there
       the bottom IS the far end of the surface stick, and designating it is how the reader
       flips the direction. */
    const field =
      pickRole === 'bottom' && selected.length > 1
        ? bottomPickableEnds(pickable, surface?.slotId)
        : pickable;
    const picked = nearestPickableEnd({
      points: field,
      click,
      role: pickRole,
      validEnds: validBottomEnds,
      toleranceFt: PICK_TOLERANCE_FT,
    });
    if (!picked) return;

    onPick(pickRole, picked);
  };

  return (
    <CenteredMap
      geometry={focus}
      className={styles.map}
      mapConfigAtom={pickerMapConfigAtom}
      // `checked: false` above is only the initial value: the layer selector re-derives it from
      // the per-status options (all on by default), so one click there would switch the whole
      // wells tile layer on and bury the picker's sticks under every existing well's stick.
      disableWellsLayer
      onClick={handleClick}
    >
      <KeepMapSized />

      {/* Unselected sticks, in the same teal dash the Unit Builder and the Type Curve map
          draw an LTD in — the reader should not have to learn a second visual language for
          the same object just because they reached it through this sheet. */}
      <Source id="ltd-context" type="geojson" data={context}>
        <Layer
          id="ltd-context-line"
          type="line"
          paint={{
            ...LTD_SLOT_LINE_PAINT,
            'line-width': LTD_SLOT_WIDTH_EDITING,
          }}
        />
      </Source>

      {/* The backend's trajectory, under everything. Grey, because every LTD line on this
          map is now teal: colour is what separates the derived composite from the sticks it
          was built from, where the dash alone used to. Still dashed, and the stretches no
          lateral covers remain the derived ties — invented rather than surveyed. */}
      <Source id="ltd-trajectory" type="geojson" data={trajectoryData}>
        <Layer
          id="ltd-trajectory-line"
          type="line"
          paint={{
            'line-color': '#525252',
            'line-width': 2,
            'line-dasharray': [2, 2],
          }}
        />
      </Source>

      {/* The traversed sticks, in the Unit Builder's selected-LTD treatment: two steps
          darker than the unselected teal, heavier, on a tighter dash. */}
      <Source id="ltd-laterals" type="geojson" data={laterals}>
        <Layer
          id="ltd-laterals-line"
          type="line"
          paint={LTD_SLOT_SELECTED_LINE_PAINT}
        />
      </Source>

      {/* The other ends the bottom hole could take: hollow green where the chain accepts
          it, greyed where it would be INCONSISTENT_BOTTOM_HOLE. */}
      <Source id="ltd-bottom-candidates" type="geojson" data={bottomCandidates}>
        <Layer
          id="ltd-bottom-candidates-point"
          type="circle"
          paint={{
            'circle-radius': 5,
            'circle-color': ['case', ['get', 'valid'], '#ffffff', '#c6c6c6'],
            'circle-stroke-width': 2,
            'circle-stroke-color': [
              'case',
              ['get', 'valid'],
              '#24a148',
              '#a8a8a8',
            ],
            'circle-opacity': ['case', ['get', 'valid'], 1, 0.6],
          }}
        />
      </Source>

      <Source id="ltd-holes" type="geojson" data={holes}>
        <Layer
          id="ltd-holes-point"
          type="circle"
          paint={{
            'circle-radius': 6,
            'circle-color': [
              'match',
              ['get', 'role'],
              'surface',
              '#da1e28',
              'bottom',
              '#24a148',
              '#8d8d8d',
            ],
            'circle-stroke-width': 2,
            'circle-stroke-color': '#ffffff',
          }}
        />
      </Source>
    </CenteredMap>
  );
}
